import { Plan, PlanCategory, PlanType } from "../../apis/subscriptionApi";
import { getBillingOrSessionCycleOptions } from "../../utils/billingOrSessionCycleOptions.utils";
import { formatToUSD } from "../../utils/misc";

export function formatPlanType(planType: PlanType) {
  switch (planType) {
    case "recurring":
      return "Recurring";
    case "non-recurring":
      return "Non-Recurring";
    case "session-pack":
      return "Punchcard";
    default:
      return "-";
  }
}

export function formatMembership(
  subscriptionPlanItemFragment: Plan,
) {
  if (subscriptionPlanItemFragment.clientStatus === "member") {
    return "Member";
  }

  if (subscriptionPlanItemFragment.clientStatus === "lead") {
    return "Lead";
  }

  return "Non-member";
}

export function formatPriceDescription(
  subscriptionPlanItemFragment: Plan,
  currency: string,
) {

  let price = subscriptionPlanItemFragment.amount;
  let intervalType = subscriptionPlanItemFragment.intervalType;
  let interval = subscriptionPlanItemFragment.interval;

  if(subscriptionPlanItemFragment.prices?.length > 0) {
    //find cheapest price
    const cheapestPrice = subscriptionPlanItemFragment.prices.reduce((min, price) => {
      return price.amount < min.amount ? price : min;
    }, subscriptionPlanItemFragment.prices[0]);

    price = cheapestPrice.amount;
    intervalType = cheapestPrice.intervalType;
    interval = cheapestPrice.interval;
  }

  if (subscriptionPlanItemFragment.type !== "recurring") {
    return formatToUSD(price, currency);
  }

  return `${formatToUSD(price, currency)} / ${getBillingOrSessionCycleOptions(
    intervalType,
    interval,
  )}`;
}

export const UNCATEGORIZED_CATEGORY = "Uncategorized";
export const GENERAL_CATEGORY_UUID = "plcat_0000000000000";
export const GENERAL_CATEGORY_NAME = "General";

export function combineSubscriptionPlansByCategory(
  categories: PlanCategory[],
  subscriptionPlans: Plan[],
) {
  // create a map of plans by category uuid
  // if they do not have category, store in plansGeneral
  const plansGeneral: Plan[] = [];
  const plansByCategoryMap: Record<string, Plan[]> = {};
  subscriptionPlans.forEach((plan) => {
    const category = plan.planCategory;
    if (!category || !category.name || !category.uuid) {
      plansGeneral.push(plan);
    } else {
      if (!plansByCategoryMap[category.uuid]) {
        plansByCategoryMap[category.uuid] = [];
      }
      plansByCategoryMap[category.uuid].push(plan);
    }
  });

  // if there are plans without category, add a category for them
  const categoriesWithUnCategorized = [...categories];
  
  // if there are general plans, create the category if needed
  if (plansGeneral.length > 0) {
    if (!plansByCategoryMap[GENERAL_CATEGORY_UUID]) {
      categoriesWithUnCategorized.push({
        name: GENERAL_CATEGORY_NAME,
        uuid: GENERAL_CATEGORY_UUID,
        slug: GENERAL_CATEGORY_NAME,
      });
      plansByCategoryMap[GENERAL_CATEGORY_UUID] = plansGeneral;
    } else {
      plansByCategoryMap[GENERAL_CATEGORY_UUID].push(...plansGeneral);
    }
  }

  // Add plan categories that don't exist in the categories list. Some plans have deleted categories
  const allCategories = categoriesWithUnCategorized;
  subscriptionPlans.forEach((plan) => {
    const category = plan.planCategory;
    if (category && category.name && category.uuid) {
      const categoryExists = allCategories.some(
        (c) => c.uuid === category.uuid,
      );
      if (!categoryExists) {
        allCategories.push({
          uuid: category.uuid,
          name: category.name,
          slug: category.name,
        });
      }
    }
  });

  // Sort categories, force UNCATEGORIZED_CATEGORY to be the first one
  const sortedCategories = allCategories.sort((a, b) => {
    if (a.name === UNCATEGORIZED_CATEGORY) return -1;
    if (b.name === UNCATEGORIZED_CATEGORY) return 1;
    return a.name.trim().localeCompare(b.name.trim());
  });

  // return a list of categories with plans
  const result: { category: PlanCategory; plans: Plan[] }[] = [];
  sortedCategories.forEach((category) => {
    const plans = plansByCategoryMap[category.uuid] || [];
    plans.sort((a, b) => a.name.localeCompare(b.name));
    result.push({
      category,
      plans,
    });
  });
  return result;
}
